// Removal support for the bulk upload (see docs/superpowers/specs/2026-08-14-bulk-upload-remove-fields-design.md).
// CommonJS so the plain-Node unit tests can require it without a bundler.

const REMOVE_KEYWORD = '#REMOVE'
const REMOVE_ALL_OPTION = "REMOVE ALL of this SKU's existing data for this section"
const REMOVE_ALL_LINKS_OPTION = "REMOVE ALL this SKU's existing links for this section"

// Put into the data in place of a value, then turned into a real deletion once all the
// merging is done. It can't just be an empty string: Utils.mergeDeep throws those away.
// Using our own private text also means only the columns this feature supports can ask
// for a deletion — a "#REMOVE" typed into any other column stays ordinary text.
const REMOVE_FIELD_FLAG = '__ST_REMOVE_FIELD__'
const CLEAR_LIST_FLAG = '__ST_REMOVE_LIST__'
// Empties a field that holds one value per language. The editor and the landing page read a
// language straight out of these fields, so taking the field away leaves them with nothing
// to read and the whole section stops rendering.
const CLEAR_OBJECT_FLAG = '__ST_REMOVE_OBJECT__'
// Same, for the portion size and its unit, which carry a fallback under "default" alongside
// any per-country entries. Emptying that entry rather than dropping it leaves exactly what
// the editor writes when someone clears the field by hand.
const CLEAR_DEFAULT_VALUE_FLAG = '__ST_REMOVE_DEFAULT_VALUE__'

const REMOVE_COLUMN = 'remove_all_existing_data'
const REMOVE_KEY_COLUMN = 'remove_all_existing_data_key'

function isRemoveKeyword (value) {
  return typeof value === 'string' && value.trim().toUpperCase() === REMOVE_KEYWORD
}

function stripRemoveKeywords (commaSeparatedString) {
  return String(commaSeparatedString)
    .split(',')
    .map((segment) => segment.trim())
    .filter((segment) => segment && !isRemoveKeyword(segment))
}

function normalizeRemoveColumnValue (value) {
  return value.trim().toUpperCase().replace(/[‘’]/g, "'").replace(/\s+/g, ' ')
}

function isRemoveAllRow (entry) {
  if (isRemoveKeyword(entry[REMOVE_KEY_COLUMN])) return true
  const value = entry[REMOVE_COLUMN]
  if (typeof value !== 'string') return false
  const normalized = normalizeRemoveColumnValue(value)
  if (!normalized) return false
  return normalized === REMOVE_KEYWORD || normalized === normalizeRemoveColumnValue(REMOVE_ALL_OPTION)
}

// Same as isRemoveAllRow, for the Additional Information sheet's separate links column.
function isRemoveAllLinksRow (entry) {
  if (isRemoveKeyword(entry.remove_all_links_key)) return true
  const value = entry.remove_all_links
  if (typeof value !== 'string') return false
  const normalized = normalizeRemoveColumnValue(value)
  if (!normalized) return false
  return normalized === REMOVE_KEYWORD || normalized === normalizeRemoveColumnValue(REMOVE_ALL_LINKS_OPTION)
}

function hasDataCells (entry) {
  return Object.keys(entry).some((columnKey) => {
    if (columnKey === 'sku' || columnKey === REMOVE_COLUMN || columnKey === REMOVE_KEY_COLUMN) return false
    const value = entry[columnKey]
    if (value === undefined || value === null) return false
    const text = String(value).trim()
    return text !== '' && text !== '#N/A' && text !== '#VALUE!'
  })
}

// A row asking for removal has its own data cells ignored. A SKU's list is only cleared
// when none of its rows carry real data — one filled-in row anywhere wins over the request.
function collectSkusToClear (sheetRows) {
  const flaggedSkus = new Set()
  const dataSkus = new Set()
  sheetRows.forEach((entry) => {
    const sku = entry.sku !== undefined && entry.sku !== null ? String(entry.sku).trim() : ''
    if (!sku) return
    if (isRemoveAllRow(entry)) {
      flaggedSkus.add(sku)
    } else if (hasDataCells(entry)) {
      dataSkus.add(sku)
    }
  })
  const removalSkus = new Set()
  flaggedSkus.forEach((sku) => {
    if (!dataSkus.has(sku)) removalSkus.add(sku)
  })
  return removalSkus
}

function applyFlaggedRemovals (node) {
  if (Array.isArray(node)) {
    node.forEach(applyFlaggedRemovals)
    return node
  }
  if (node && typeof node === 'object') {
    Object.keys(node).forEach((key) => {
      if (node[key] === REMOVE_FIELD_FLAG) {
        delete node[key]
      } else if (node[key] === CLEAR_LIST_FLAG) {
        node[key] = []
      } else if (node[key] === CLEAR_OBJECT_FLAG) {
        node[key] = {}
      } else if (node[key] === CLEAR_DEFAULT_VALUE_FLAG) {
        node[key] = { default: '' }
      } else {
        applyFlaggedRemovals(node[key])
      }
    })
  }
  return node
}

function hasAnyContent (translationsObject) {
  return !!translationsObject && typeof translationsObject === 'object' &&
    Object.keys(translationsObject).length > 0
}

function dropEmptiedEntries (elabelObject) {
  const productInfos = elabelObject['product-infos']
  if (productInfos && Array.isArray(productInfos.custom_fields)) {
    productInfos.custom_fields = productInfos.custom_fields.filter((field) =>
      hasAnyContent(field.label) || hasAnyContent(field.value))
  }
  const brandInfos = elabelObject['brand-infos']
  if (brandInfos && Array.isArray(brandInfos.links)) {
    brandInfos.links = brandInfos.links.filter((item) =>
      (item.link && String(item.link).trim() !== '') || hasAnyContent(item.text))
  }
  const businessOperator = elabelObject['business-operator']
  if (businessOperator && typeof businessOperator === 'object' && !Array.isArray(businessOperator)) {
    Object.keys(businessOperator).forEach((operatorKey) => {
      const operator = businessOperator[operatorKey]
      if (operator && typeof operator === 'object' && !Array.isArray(operator) && Object.keys(operator).length === 0) {
        delete businessOperator[operatorKey]
      }
    })
  }
  return elabelObject
}

module.exports = {
  REMOVE_KEYWORD,
  REMOVE_ALL_OPTION,
  REMOVE_ALL_LINKS_OPTION,
  REMOVE_FIELD_FLAG,
  CLEAR_LIST_FLAG,
  CLEAR_OBJECT_FLAG,
  CLEAR_DEFAULT_VALUE_FLAG,
  isRemoveKeyword,
  stripRemoveKeywords,
  isRemoveAllRow,
  isRemoveAllLinksRow,
  hasDataCells,
  collectSkusToClear,
  applyFlaggedRemovals,
  dropEmptiedEntries
}
